import type { User } from 'payments-api/model';
import {
  useGetUsersSuspense,
  usePostUsers,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';

const TitleRow = ({ title }: { title: string }) => (
  <div className="flex flex-row items-center h-fit border-main border-b-[1px] py-f1 w-full">
    <span className="text-main">{title}</span>
  </div>
);

const UserRow = ({
  user,
  goToUser,
}: {
  user: User;
  goToUser: (user: User) => void;
}) => (
  <button
    type="button"
    aria-label={`user-details-${user.externalId}`}
    onClick={() => goToUser(user)}
    className={clsx(
      'flex flex-row items-center justify-between h-f4 pl-f1 border-main border-[1px] border-t-0 w-full overflow-y-hidden hover:bg-negative'
    )}
  >
    <span className="text-main truncate">{user.externalId}</span>
    <span className="w-f2 text-center">↗</span>
  </button>
);

const NewUserRow = ({ onAdd }: { onAdd: () => void }) => (
  <button
    type="button"
    className="flex w-full h-f4 items-center px-f1 bg-transparent border-main border-[1px] border-t-0 text-main-500 hover:text-main hover:bg-negative"
    onClick={onAdd}
    aria-label="Add new user"
    data-testid="add-new-user-button"
  >
    Add+
  </button>
);

const DraftUserRow = ({
  selectedRoleName,
  onSubmit,
  onCancel,
  isPending,
}: {
  selectedRoleName: string;
  onSubmit: (externalId: string, role: string) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const [externalId, setExternalId] = useState('');
  const [role, setRole] = useState(selectedRoleName);
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    setRole(selectedRoleName);
  }, [selectedRoleName]);
  const handleSubmit = useCallback(async () => {
    setError(undefined);
    try {
      await onSubmit(externalId.trim(), role.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  }, [externalId, role, onSubmit]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-y-f1 w-full border-main border-[1px] border-t-0 p-f1"
    >
      <TextInput
        padding="p-0"
        width="w-full"
        height="h-fit"
        align="left"
        placeholder="External ID"
        size="md"
        value={externalId}
        onChange={(e) => setExternalId(e.target.value)}
        data-testid="draft-user-external-id"
      />
      {error && <ErrorText>{error}</ErrorText>}
      <FormButtons
        type="secondary"
        cancelButtonLabel="Cancel"
        onCancel={onCancel}
        submitButtonLabel={isPending ? 'Creating...' : 'Create'}
        disabled={isPending || !externalId.trim() || !role.trim()}
        submitButtonTestId="draft-user-submit-button"
        cancelButtonTestId="draft-user-cancel-button"
      />
    </form>
  );
};

export const CatalogUserTableContent = ({
  workspaceId,
  selectedRoleName,
}: {
  workspaceId: string;
  selectedRoleName: string;
}) => {
  const { data: usersData } = useGetUsersSuspense();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const createUserMutation = usePostUsers();
  const [showDraftUser, setShowDraftUser] = useState(false);

  const users: User[] = usersData.data;
  const filteredUsers = users.filter((user) => user.role === selectedRoleName);

  const goToUser = useCallback(
    (user: User) => {
      const url = makePath(
        ['w', workspaceId, 'payments', 'users', user.externalId],
        { absolute: true }
      );
      setLocation(url);
    },
    [workspaceId, setLocation]
  );

  const handleDraftUserSubmit = useCallback(
    async (newExternalId: string, newRole: string) => {
      await createUserMutation.mutateAsync({
        data: {
          externalId: newExternalId,
          role: newRole.trim(),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/users'] });
      setShowDraftUser(false);
    },
    [createUserMutation, queryClient]
  );
  return (
    <div className="flex flex-col h-fit w-full">
      {filteredUsers.map((user) => (
        <UserRow key={user.id} user={user} goToUser={goToUser} />
      ))}
      {showDraftUser ? (
        <DraftUserRow
          selectedRoleName={selectedRoleName}
          onSubmit={handleDraftUserSubmit}
          onCancel={() => setShowDraftUser(false)}
          isPending={createUserMutation.isPending}
        />
      ) : (
        <NewUserRow onAdd={() => setShowDraftUser(true)} />
      )}
    </div>
  );
};

type CatalogUserTableProps = {
  workspaceId: string;
  selectedRoleName: string;
};

export const CatalogUserTable = ({
  workspaceId,
  selectedRoleName,
}: CatalogUserTableProps): JSX.Element => (
  <div className="flex flex-col h-fit w-full">
    <TitleRow title="Users" />
    <Suspense
      fallback={
        <div className="flex h-f4 w-full border-main border-[1px] border-t-0 p-f1 text-main-500">
          Loading...
        </div>
      }
    >
      <CatalogUserTableContent
        workspaceId={workspaceId}
        selectedRoleName={selectedRoleName}
      />
    </Suspense>
  </div>
);
